/**
 * Paperwork and photos on the new job page (go core, 11/3). Before this, the
 * page had a "N files" button that counted plain documents only (so a job with
 * six test reports said "0 files") and links to `?page=classic#paperwork` and
 * `#photos`, anchors that don't exist.
 *
 * Now the page carries the classic Paperwork card and Photos card with the
 * classic wiring: Upload (a test report files itself to its device: the
 * "attach a test report" the office does ~550 times a month), Match all
 * reports, make official, remove a report, delete a document, regenerate,
 * fix the WO template, send an invoice, resend the packet (after a first send
 * only). Same endpoints and query keys as classic, so both pages stay in step.
 */
import { useMemo, useState } from "react";
import { useMutation, useQueries, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { finishLineKey } from "@/lib/finish-line";
import { useSendHistory } from "@/lib/use-send-history";
import { SendCustomerDialog } from "@/components/send-customer-dialog";
import { ResendPacketDialog } from "@/components/resend-packet-dialog";
import { useMakeOfficial, type ReportDeviceCandidate } from "@/components/report-device-picker";
import { useReportMatchAll } from "@/components/report-match-all";
import { PaperworkCard } from "../detail/paperwork-card";
import { JobPhotosCard } from "../detail/photos-card";
import { useUseAsOriginalWorkOrder, type WorkOrderOffer } from "../detail/no-work-order-banner";
import { apiErrorInfo, visibleJobAssets, type JobDetailResponse, type ReportAttachment } from "../detail/types";
import { packetSubjectFor } from "../jobDetailHelpers";
import { canResendPacket, mayHaveSentPacket } from "./fresh-more";

/** The eight attachment lists the Paperwork card shows, by the classic page's query keys. */
export const PAPERWORK_LISTS = [
    { key: "documents", category: "document" },
    { key: "test-reports", category: "test_report" },
    { key: "official-test-reports", category: "test_report_official" },
    { key: "inspection-reports", category: "report" },
    { key: "summary-reports", category: "summary" },
    { key: "work-orders-filled", category: "work_order_filled" },
    { key: "closeout-uploads", category: "closeout_upload" },
    { key: "work-orders-uploaded", category: "work_order_uploaded" },
] as const;
type ListKey = (typeof PAPERWORK_LISTS)[number]["key"];

/**
 * Every paperwork list on the job, shared by the Paperwork card and the
 * "N files" count, so the count is always the number of files the card shows.
 */
export function usePaperworkLists(jobId: string) {
    const results = useQueries({
        queries: PAPERWORK_LISTS.map((l) => ({
            queryKey: ["job", jobId, l.key],
            queryFn: () =>
                api.get<{ attachments: ReportAttachment[] }>(`/api/attachments?jobId=${jobId}&category=${l.category}`),
            enabled: !!jobId,
        })),
    });
    const lists = {} as Record<ListKey, ReportAttachment[]>;
    PAPERWORK_LISTS.forEach((l, i) => (lists[l.key] = results[i]!.data?.attachments ?? []));
    const loaded = results.every((r) => !r.isLoading);
    const total = countFiles(lists);
    return { lists, total, loaded };
}

export function countFiles(lists: Partial<Record<ListKey, unknown[]>>): number {
    return PAPERWORK_LISTS.reduce((n, l) => n + (lists[l.key]?.length ?? 0), 0);
}

export const PAPERWORK_ANCHOR = "fresh-paperwork";
export const PHOTOS_ANCHOR = "fresh-photos";

export function scrollToSection(anchor: string) {
    document.getElementById(anchor)?.scrollIntoView({ behavior: "smooth", block: "start" });
}

export function FreshPaperwork({ data }: { data: JobDetailResponse }) {
    const job = data.job;
    const id = job.id;
    const qc = useQueryClient();
    const { isOffice } = useAuth();
    const { lists } = usePaperworkLists(id);
    const [uploading, setUploading] = useState(false);
    const [sendInvoiceId, setSendInvoiceId] = useState<string | null>(null);
    const [resendOpen, setResendOpen] = useState(false);

    const sendHistoryQ = useSendHistory(id, isOffice && mayHaveSentPacket(job.status));
    const resendable = canResendPacket({ isOffice, everSent: sendHistoryQ.data?.everSent });

    const makeOfficial = useMakeOfficial(id);
    const matchAll = useReportMatchAll(id);
    const useAsOriginalWo = useUseAsOriginalWorkOrder(id);

    const assets = visibleJobAssets(job.trade_code ?? null, data.assets ?? []);
    const tests = data.testResults ?? [];
    const devicePaperwork = useMemo(
        () => ({
            devices: assets.map((a) => ({
                assetId: a.asset_id,
                serial: a.serial_number,
                size: a.size_label,
                assemblyType: a.assembly_type ?? null,
            })),
            tests: tests.map((t) => ({
                assetId: t.asset_id,
                serial: t.serial_number ?? null,
                result: t.overall_result,
            })),
        }),
        [assets, tests],
    );

    const refreshJob = () => {
        qc.invalidateQueries({ queryKey: ["job", id] });
        qc.invalidateQueries({ queryKey: ["jobs"] });
        qc.invalidateQueries({ queryKey: ["activity-timeline"] });
    };

    const deleteDoc = useMutation({
        mutationFn: (attachmentId: string) => api.delete(`/api/attachments/${attachmentId}`),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["job", id, "documents"] });
            qc.invalidateQueries({ queryKey: finishLineKey(id) });
            toast.success("Document deleted");
        },
        onError: (e: unknown) => {
            const { error: code, message } = apiErrorInfo(e);
            toast.error(`Delete failed: ${message ?? code ?? "unknown"}`);
        },
    });

    // The classic page's upload, step for step (detail.tsx handleDocUpload):
    // a PDF that is really a test report files itself as that device's report.
    const upload = async (files: FileList | null) => {
        if (!files || files.length === 0) return;
        setUploading(true);
        try {
            let filedReports = 0;
            let plainDocs = 0;
            let needsDevice = 0;
            for (const file of Array.from(files)) {
                const fd = new FormData();
                fd.append("file", file);
                fd.append("jobId", id);
                fd.append("category", "document");
                const r = await api.postForm<{
                    attachment?: { id: string; file_name: string } | null;
                    filedAsReport?: { serial: string; label: string; duplicate?: boolean } | null;
                    reportNotice?: string | null;
                    needsDevice?: { attachmentId: string; candidates: ReportDeviceCandidate[] } | null;
                    notReport?: { attachmentId: string; docClass: string } | null;
                    workOrderOffer?: WorkOrderOffer | null;
                }>("/api/attachments/upload", fd);
                if (r?.filedAsReport) {
                    filedReports += 1;
                    toast.success(
                        r.filedAsReport.duplicate
                            ? `That report for S/N ${r.filedAsReport.serial} was already on the job. Nothing added.`
                            : `Filed as the test report for S/N ${r.filedAsReport.serial}`,
                    );
                } else if (r?.needsDevice) {
                    needsDevice += 1;
                } else {
                    plainDocs += 1;
                    if (r?.workOrderOffer) {
                        useAsOriginalWo.offer(r.workOrderOffer, file.name);
                        plainDocs -= 1;
                    } else if (r?.notReport && file.name.toLowerCase().endsWith(".pdf")) {
                        const asReport = { id: r.notReport.attachmentId, file_name: file.name };
                        toast.success(
                            r.notReport.docClass === "work_order"
                                ? `${file.name} reads as a work order, not a city report. Saved as a document.`
                                : `${file.name} saved as a document.`,
                            {
                                duration: 9000,
                                action: { label: "It's a report", onClick: () => makeOfficial.start(asReport) },
                            },
                        );
                        plainDocs -= 1;
                    } else if (r?.reportNotice) toast.warning(r.reportNotice, { duration: 9000 });
                }
            }
            if (plainDocs > 0 && filedReports === 0) {
                toast.success(`${plainDocs} document${plainDocs === 1 ? "" : "s"} uploaded`);
            }
            if (needsDevice > 0) matchAll.open();
            qc.invalidateQueries({ queryKey: ["job", id, "documents"] });
            qc.invalidateQueries({ queryKey: ["job", id, "official-test-reports"] });
            qc.invalidateQueries({ queryKey: finishLineKey(id) });
            qc.invalidateQueries({ queryKey: ["close-out-queue"] });
            qc.invalidateQueries({ queryKey: ["activity-timeline"] });
        } catch (err: unknown) {
            const { message } = apiErrorInfo(err);
            toast.error(`Upload failed: ${message ?? (err instanceof Error ? err.message : "unknown")}`);
        } finally {
            setUploading(false);
        }
    };

    return (
        <>
            <div id={PAPERWORK_ANCHOR} className="scroll-mt-4" data-testid="fresh-paperwork">
                <PaperworkCard
                    jobId={id}
                    jobNumber={job.display_number ?? null}
                    customerName={job.company_name ?? null}
                    tradeCode={job.trade_code ?? null}
                    documents={lists.documents}
                    testReports={lists["test-reports"]}
                    officialReports={lists["official-test-reports"]}
                    inspectionReports={lists["inspection-reports"]}
                    summaryReports={lists["summary-reports"]}
                    workOrders={lists["work-orders-filled"]}
                    closeoutUploads={lists["closeout-uploads"]}
                    uploadedWorkOrders={lists["work-orders-uploaded"]}
                    woNumber={job.briefing_wo_number ?? null}
                    isWorkOrderJob={job.kind === "vendor_work_order"}
                    customerId={job.customer_id ?? null}
                    assignedTemplateId={data.workOrder?.template_id ?? null}
                    invoices={data.invoices ?? []}
                    estimates={data.estimates ?? []}
                    isUploading={uploading}
                    onUpload={(files) => void upload(files)}
                    onDeleteDocument={(attachmentId) => deleteDoc.mutate(attachmentId)}
                    deletePending={deleteDoc.isPending}
                    onSendInvoice={setSendInvoiceId}
                    sendHistory={sendHistoryQ.data ?? null}
                    onResendPacket={resendable ? () => setResendOpen(true) : undefined}
                    onMatchReports={matchAll.open}
                    devicePaperwork={devicePaperwork}
                />
            </div>
            <div id={PHOTOS_ANCHOR} className="scroll-mt-4" data-testid="fresh-photos">
                <JobPhotosCard jobId={id} />
            </div>
            {makeOfficial.element}
            {matchAll.element}
            {sendInvoiceId && (
                <SendCustomerDialog
                    docType="invoice"
                    id={sendInvoiceId}
                    customerId={job.customer_id ?? null}
                    open={!!sendInvoiceId}
                    onOpenChange={(o) => !o && setSendInvoiceId(null)}
                    onSent={refreshJob}
                />
            )}
            {resendOpen && resendable && (
                <ResendPacketDialog
                    open
                    onClose={() => setResendOpen(false)}
                    jobId={id}
                    customerId={job.customer_id ?? null}
                    defaultSubject={packetSubjectFor(job)}
                    startInMissedMode={false}
                    onSent={refreshJob}
                    onGoToPaperwork={() => {
                        setResendOpen(false);
                        scrollToSection(PAPERWORK_ANCHOR);
                    }}
                />
            )}
        </>
    );
}
